import React, { FC, useEffect, useState } from 'react';
import { first, isEmpty } from 'lodash';
import {
  Checkbox,
  CircularProgress,
  ListItemText,
  Modal,
  OutlinedInput,
} from '@material-ui/core';
import {
  Formik,
  USER_LEVEL,
  USER_LEVEL_LABEL,
  useFormikContext,
  useStores,
  yup,
} from '~/utils';
import If from '../If';
import {
  Button,
  Content,
  Field,
  IconStyled,
  LevelUserText,
  Option,
  SelectStyled,
  TextError,
} from './styles';

const validate = yup.object().shape({
  email: yup.string().email('Email inválido').required('Email obrigatório'),
  password: yup
    .string()
    .min(6, 'Senha deve conter no mínimo 6 caracteres')
    .required('Senha obrigatória'),
  name: yup
    .string()
    .min(3, 'Nome deve conter no mínimo 3 caracteres')
    .required('Nome obrigatório'),
  level: yup.string().required('Nível obrigatório'),
});

type Props = {
  open?: boolean;
  onCloseModal?: () => void;
  optionsPermissions?: ProcessType.OptionsPermissions[];
};

const ModalAddUser: FC<Props> = ({ open, onCloseModal = () => {} }) => {
  const { user, process } = useStores();

  const onSubmit = async (data: FirebaseType.CreateUserParams) => {
    await user.create(data);
    onCloseModal();
  };

  useEffect(() => {
    process.list();
  }, []);

  return (
    <Formik
      onSubmit={onSubmit}
      validationSchema={validate}
      initialValues={{
        name: '',
        email: '',
        level: '',
        password: '',
        permissions: [],
      }}
    >
      <Children
        onCloseModal={onCloseModal}
        open={open}
        optionsPermissions={process.enumsPermissions}
      />
    </Formik>
  );
};

const Children: FC<Props> = ({
  onCloseModal,
  open = false,
  optionsPermissions,
}) => {
  const [selected, setSelected] = useState<string[]>([]);

  const {
    values,
    errors,
    handleChange,
    setFieldValue,
    submitForm,
    isSubmitting,
  } = useFormikContext<FirebaseType.CreateUserParams>();

  const handlePermissions = (event: Record<string, any>) => {
    const value =
      typeof event.target.value === 'string'
        ? event.target.value.split(',')
        : event.target.value;

    setSelected(value);

    setFieldValue('permissions', value);
  };

  return (
    <Modal open={open}>
      <Content>
        <IconStyled onClick={onCloseModal}>close</IconStyled>
        <Field
          placeholder="Nome Completo"
          name="name"
          type="text"
          onChange={handleChange('name')}
          value={values.name}
          error={!!errors.name}
        />

        <Field
          placeholder="Email"
          name="email"
          type="email"
          onChange={handleChange('email')}
          value={values.email}
        />

        <Field
          placeholder="Senha"
          name="password"
          type="password"
          onChange={handleChange('password')}
          value={values.password}
        />

        <LevelUserText>Nível do usuário</LevelUserText>
        <SelectStyled
          onChange={(e: any) => setFieldValue('level', e.target.value)}
          value={values.level}
        >
          {[USER_LEVEL.ADMIN, USER_LEVEL.READER].map((option) => (
            <Option key={option} value={option || ''}>
              {option === USER_LEVEL.ADMIN
                ? USER_LEVEL_LABEL.ADMIN
                : USER_LEVEL_LABEL.READER}
            </Option>
          ))}
        </SelectStyled>

        <LevelUserText>Permissões</LevelUserText>
        <SelectStyled
          multiple
          value={selected}
          onChange={(e: Record<string, any>) => handlePermissions(e)}
          input={<OutlinedInput label="Tag" />}
          renderValue={(selected: any = []) => {
            const option = selected.map((uid: string) => {
              const pp = first(
                optionsPermissions?.filter(
                  ({ processId }) => processId === uid,
                ),
              ) as any;
              return `${pp.name}, `;
            });

            return option;
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 48 * 4.5 + 8,
                width: 250,
              },
            },
          }}
        >
          {optionsPermissions?.map(({ name, processId, processName }) => (
            <Option key={processId} value={processId}>
              <Checkbox checked={selected.indexOf(processId) > -1} />
              <ListItemText primary={`${name} - ${processName}`} />
            </Option>
          ))}
        </SelectStyled>

        <TextError>
          {errors.name ||
            errors.email ||
            errors.password ||
            errors.level ||
            ' '}
        </TextError>

        <Button
          onClick={submitForm}
          disabled={isEmpty(values.email) || isEmpty(values.password)}
        >
          <If condition={!isSubmitting}> Criar Usuário</If>
          <If condition={isSubmitting}>
            <CircularProgress size={20} style={{ color: 'white' }} />
          </If>
        </Button>
      </Content>
    </Modal>
  );
};
export default ModalAddUser;
